import React, { useCallback, useEffect, useState } from "react";
import { Alert, DataTable, Modal, Spinner } from "@tag/tag-components-react-v4";
import { ProjectAccents } from "../../General/config/projectAccents";
import SurveyJSTemplateService from "../services/SurveyJSTemplateService";
import { TenantProvisioningAttemptDto } from "../types/SurveyJSTemplate";
import styles from "./SurveyJSTemplates.module.scss";

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatDateTime(value: unknown): string {
    if (typeof value !== "string" || value.length === 0) return "";
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return `${d.getDate()} ${MONTH_NAMES[d.getMonth()]} ${String(d.getFullYear()).slice(2)} `
        + `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export interface HistoryTarget {
    templateGuid: string;
    templateName: string;
    templateVersion: string;
    corpSiteName: string;
    clientId: string | number;
    tenantDisplayName: string;
}

interface Props {
    visible: boolean;
    target: HistoryTarget | null;
    onClose: () => void;
}

function TenantProvisioningHistoryModal({ visible, target, onClose }: Props) {
    const [attempts, setAttempts] = useState<TenantProvisioningAttemptDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | undefined>();

    const load = useCallback(async () => {
        if (!target) return;
        setLoading(true);
        setError(undefined);
        try {
            const list = await SurveyJSTemplateService.getTenantProvisioningHistory(
                target.templateGuid, target.corpSiteName, target.clientId
            );
            setAttempts(list);
        } catch (e: any) {
            setError(e?.message || "Failed to load provisioning history.");
            setAttempts([]);
        } finally {
            setLoading(false);
        }
    }, [target]);

    useEffect(() => {
        if (visible && target) load();
        if (!visible) setAttempts([]);
    }, [visible, target, load]);

    const columns = React.useMemo(() => [
        {
            accessorKey: "startedUtc",
            header: "Started",
            cell: ({ getValue }: any) => <>{formatDateTime(getValue())}</>,
        },
        {
            id: "finished",
            header: "Finished",
            cell: ({ row }: any) => {
                const a: TenantProvisioningAttemptDto = row.original;
                const ts = a.erroredUtc ?? a.completedUtc;
                return <>{ts ? formatDateTime(ts) : "—"}</>;
            },
        },
        {
            accessorKey: "status",
            header: "Status",
            cell: ({ getValue }: any) => {
                const s = getValue() as TenantProvisioningAttemptDto["status"];
                return <>{s === "InProgress" ? "In progress" : s}</>;
            },
        },
        { accessorKey: "initiatorName", header: "By" },
        { accessorKey: "triggerKind", header: "Trigger" },
        {
            accessorKey: "version",
            header: "Version",
            cell: ({ getValue }: any) => <>{getValue() ?? "—"}</>,
        },
        {
            accessorKey: "errorMessage",
            header: "Error",
            cell: ({ getValue }: any) => {
                const msg = getValue() as string | null;
                return msg
                    ? <span title={msg}>{msg.length > 60 ? `${msg.substring(0, 60)}…` : msg}</span>
                    : <>—</>;
            },
        },
    ], []);

    const heading = target
        ? `Provisioning history — ${target.tenantDisplayName} · ${target.templateName} v${target.templateVersion}`
        : "Provisioning history";

    return (
        <Modal
            visible={visible}
            accent={ProjectAccents.CareCompliance}
            heading={heading}
            primaryButtonProps={{ text: "Close", onClick: onClose }}
            cancelButtonProps={{ buttonStyle: { display: "none" } }}
        >
            <div className={styles.HistoryModalContent}>
                {error ? <Alert type="error">{error}</Alert> : null}
                {loading ? <Spinner /> : (
                    <DataTable
                        isLoading={false}
                        columns={columns}
                        data={attempts}
                        accent={ProjectAccents.CareCompliance}
                        height="50vh"
                        noDataElement={
                            <div style={{ padding: 16, textAlign: "center", color: "#666" }}>
                                No provisioning attempts recorded for this tenant + template yet.
                            </div>
                        }
                    />
                )}
            </div>
        </Modal>
    );
}

export default TenantProvisioningHistoryModal;
